/**
 * Profesyonel oturum yönetimi - localStorage display info.
 *
 * ÖNEMLİ: Burada **token TUTULMAZ**. Token'lar HTTP-only cookie'lerde
 * (`pb_access`, `pb_refresh`) bulunur ve JavaScript'e değmez. localStorage
 * sadece sayfa yenileme sonrası kullanıcı arayüzünde flicker'ı engellemek için
 * gösterim bilgilerini saklar:
 *   { isLoggedIn, userRole, personnelId, personnelName, accessExpiresAt,
 *     credentialSetupPending, rememberMe, sessionId }
 *
 * Gerçek doğrulama her sayfa yüklemesinde `GET /api/auth/me` ile yapılır.
 */
const STORAGE_KEY = "parkBakimAuth";

const VALID_KEYS = new Set([
  "isLoggedIn",
  "userRole",
  "userRoleRaw",
  "personnelId",
  "personnelName",
  "accessExpiresAt",
  "credentialSetupPending",
  "rememberMe",
  "sessionId",
]);

function _safeParse(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch (_) { /* ignore */ }
  return null;
}

/**
 * Eski parkBakimAuth verilerinden hassas alanları temizler (token, refreshToken vb.).
 * Sadece display info kalır.
 */
export function migrateLegacyAuthStorage() {
  try {
    // sessionStorage → localStorage geçişi (eski client'larda token sessionStorage'daydı)
    if (typeof sessionStorage !== "undefined") {
      const sessionRaw = sessionStorage.getItem(STORAGE_KEY);
      if (sessionRaw) {
        const sess = _safeParse(sessionRaw);
        if (sess) {
          // Eski token'ları kesinlikle saklamayalım — backend cookie devraldı.
          const cleaned = {};
          for (const k of VALID_KEYS) {
            if (sess[k] !== undefined) cleaned[k] = sess[k];
          }
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
          }
        }
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    // localStorage'da varsa hassas alanları (token, refreshToken vb.) temizle
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = _safeParse(raw);
      if (data) {
        // Admin paneli için userRoleRaw gerekli (TB-047 alias ayrımı). Eski
        // snapshot'larda bu alan yok — admin tespiti yapamadığımız için kullanıcıyı
        // tek seferlik yeniden login'e zorla. Bu race condition'ı kökten kapatır.
        if (data.isLoggedIn && !data.userRoleRaw) {
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
        let dirty = false;
        for (const sensitive of ["token", "accessToken", "refreshToken"]) {
          if (sensitive in data) {
            delete data[sensitive];
            dirty = true;
          }
        }
        if (dirty) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
      }
    }
  } catch (_) { /* ignore */ }
}

export function readAuthSnapshot() {
  if (typeof localStorage === "undefined") return null;
  return _safeParse(localStorage.getItem(STORAGE_KEY));
}

export function writeAuthSnapshot(snapshot) {
  if (typeof localStorage === "undefined") return;
  if (!snapshot) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  const cleaned = {};
  for (const k of VALID_KEYS) {
    if (snapshot[k] !== undefined) cleaned[k] = snapshot[k];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
}

export function clearAuthSnapshot() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(STORAGE_KEY);
  }
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export const STORAGE_KEY_NAME = STORAGE_KEY;
